import { API_ENDPOINTS, RELOAD_DELAY_MS } from "@/constants";
import { useUpdateStream } from "@/hooks/useUpdateStream";
import { getApiService } from "@/services/apiService";
import { BackendTimerResponse, NextUpdateProps } from "@/types";
import { useAuth, useData } from "@/zustand/Zustand";
import { AlertTriangle, CheckCircle, Database, RefreshCw, Wifi, WifiOff, XCircle } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { reportAppError } from '@/helpers/sentry';

interface DataSourceStatus {
  wagoVersion: string;
  tsmTimestamp: number;
  blizzardTimestamp: number;
}

// Loading spinner component
const LoadingSpinner: React.FC = () => {
  const { t } = useTranslation(['common']);
  return (
    <div className="relative w-5 h-5" role="status" aria-label={t('common:nextUpdate.reloading')}>
      <div className="absolute inset-0 rounded-full border-t-2 border-b-2 border-blue-600 animate-spin" />
      <div className="absolute inset-0 rounded-full border-t-2 border-b-2 border-yellow-500 animate-spin [animation-direction:reverse] [animation-duration:1.5s]" />
    </div>
  );
};

// Connection status indicator
const ConnectionIndicator: React.FC<{ isConnected: boolean }> = ({ isConnected }) => {
  const { t } = useTranslation(['common']);
  return (
    <span title={isConnected ? t('common:nextUpdate.liveUpdatesConnected') : t('common:nextUpdate.liveUpdatesReconnecting')}>
      {isConnected ? (
        <Wifi className="w-3 h-3 text-green-500" />
      ) : (
        <WifiOff className="w-3 h-3 text-yellow-500 animate-pulse" />
      )}
    </span>
  );
};

// TSM Status Icon Component
const TsmStatusIcon: React.FC<{ timestamp: number }> = ({ timestamp }) => {
  const { t } = useTranslation(['common']);
  const [ageHours, setAgeHours] = useState(() => (Date.now() - timestamp * 1000) / (1000 * 60 * 60));

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- syncing computed age from updated timestamp prop
    setAgeHours((Date.now() - timestamp * 1000) / (1000 * 60 * 60));
  }, [timestamp]);

  if (ageHours < 24) {
    return (
      <span title={t('common:nextUpdate.tsmFresh')}>
        <CheckCircle className="w-4 h-4 text-green-500" />
      </span>
    );
  }

  if (ageHours < 25) {
    return (
      <span title={t('common:nextUpdate.tsmStale24')}>
        <AlertTriangle className="w-4 h-4 text-yellow-500" />
      </span>
    );
  }

  return (
    <span title={t('common:nextUpdate.tsmStale25')}>
      <XCircle className="w-4 h-4 text-red-500" />
    </span>
  );
};

const NextUpdate: React.FC<NextUpdateProps> = ({ onImminentReload }) => {
  const { t } = useTranslation(['common']);
  const api = getApiService();
  const { userData, setUserData } = useAuth();

  // Use Zustand for persistent lastModified
  const { lastModifiedUS, lastModifiedEU, setLastModifiedUS, setLastModifiedEU } = useData();
  const lastModified = userData?.region === 'us' ? lastModifiedUS : lastModifiedEU;
  const setLastModified = userData?.region === 'us' ? setLastModifiedUS : setLastModifiedEU;

  const [showDetailPane, setShowDetailPane] = useState<boolean>(false);
  const [dataStatus, setDataStatus] = useState<DataSourceStatus | null>(null);
  const [isReloadReady, setIsReloadReady] = useState<boolean>(false);
  const [isReloading, setIsReloading] = useState<boolean>(false);
  const [isTogglingAutoRefresh, setIsTogglingAutoRefresh] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<string>("");

  const reloadTimeoutRef = useRef<number | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<number | undefined>(undefined);
  const countdownIntervalRef = useRef<number | undefined>(undefined);

  const autoRefreshEnabled = userData?.autoRefresh ?? true;

  // Track the last timestamp we've acted on (not from Zustand, local to this component)
  const lastActedTimestampRef = useRef<number>(lastModified);

  // SSE hook - receives push updates instead of polling
  const { isConnected, reconnect } = useUpdateStream({
    enabled: !!userData?.region,
    onAuctionUpdate: useCallback((timestamp: number, _?: string) => {

      // Update data status with new timestamp
      setDataStatus(prev => prev ? {
        ...prev,
        blizzardTimestamp: timestamp
      } : null);

      // Check if this is actually a new update (newer than what we've acted on)
      if (timestamp > lastActedTimestampRef.current) {
        lastActedTimestampRef.current = timestamp;

        if (autoRefreshEnabled) {
          // Auto-refresh mode: reload after delay
          setIsReloading(true);

          if (reloadTimeoutRef.current !== undefined) {
            clearTimeout(reloadTimeoutRef.current);
          }

          reloadTimeoutRef.current = window.setTimeout(() => {
            onImminentReload();
          }, RELOAD_DELAY_MS);
        } else {
          // Manual refresh mode: enable refresh button
          setIsReloadReady(true);
        }
      }
    }, [autoRefreshEnabled, onImminentReload])
  });

  // Whether the pane was opened by hovering (as opposed to click/keyboard).
  // Hover-opened panes close on mouse leave; click-opened panes stay pinned
  // until Escape, outside click or another toggle.
  const hoverOpenedRef = useRef<boolean>(false);

  // Hover handlers (hover-open is a convenience enhancement on top of click)
  const handleMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setShowDetailPane(prev => {
      if (!prev) hoverOpenedRef.current = true;
      return true;
    });
  }, []);

  const handleMouseLeave = useCallback(() => {
    hoverTimeoutRef.current = window.setTimeout(() => {
      if (hoverOpenedRef.current) {
        hoverOpenedRef.current = false;
        setShowDetailPane(false);
      }
    }, 200);
  }, []);

  // Click / Enter / Space on the trigger toggles the pane. If the pane was
  // just opened by hover, the first click pins it open instead of closing it.
  const handleTriggerClick = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setShowDetailPane(prev => {
      if (prev && hoverOpenedRef.current) {
        hoverOpenedRef.current = false;
        return true;
      }
      hoverOpenedRef.current = false;
      return !prev;
    });
  }, []);

  // Close on Escape and on outside click while the pane is open
  useEffect(() => {
    if (!showDetailPane) return;

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        hoverOpenedRef.current = false;
        setShowDetailPane(false);
      }
    };
    const handleOutsideClick = (e: MouseEvent): void => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        hoverOpenedRef.current = false;
        setShowDetailPane(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showDetailPane]);

  // Get color based on countdown time remaining
  const getCountdownColor = useCallback((): string => {
    if (!lastModified) return "text-gray-400";

    const nextUpdateTime = lastModified * 1000 + 60 * 60 * 1000;
    const now = Date.now();
    const diff = nextUpdateTime - now;
    const minutesLeft = diff / (1000 * 60);

    if (minutesLeft <= 0) return "text-red-400";
    if (minutesLeft < 5) return "text-yellow-500";
    return "text-blue-400";
  }, [lastModified]);

  // Set up countdown interval
  useEffect(() => {
    const updateCountdown = (): void => {
      if (!lastModified) {
        setCountdown("");
        return;
      }

      const nextUpdateTime = lastModified * 1000 + 60 * 60 * 1000;
      const now = Date.now();
      const diff = nextUpdateTime - now;

      if (isReloadReady) {
        setCountdown("");
        return;
      }

      const totalSeconds = Math.floor(Math.abs(diff) / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      const sign = diff < 0 ? "-" : "";
      setCountdown(`${sign}${minutes}:${seconds.toString().padStart(2, "0")}`);
    };

    updateCountdown();
    countdownIntervalRef.current = window.setInterval(updateCountdown, 1000);

    return () => {
      if (countdownIntervalRef.current !== undefined) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [lastModified, isReloadReady]);

  // Get color based on Blizzard data freshness
  const getBlizzardColor = useCallback((timestamp: number): string => {
    const ageMinutes = (Date.now() - timestamp * 1000) / (1000 * 60);
    if (ageMinutes < 60) return "text-green-500";
    if (ageMinutes < 70) return "text-yellow-500";
    return "text-red-500";
  }, []);

  // Format timestamp to readable format
  const formatTimestamp = useCallback((timestamp: number): string => {
    const now = Date.now();
    const diff = now - timestamp * 1000;

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m ago`;
    }
    return `${minutes}m ago`;
  }, []);

  // Handle manual refresh
  const handleManualRefresh = useCallback((): void => {
    setIsReloading(true);
    setIsReloadReady(false);
    onImminentReload();
  }, [onImminentReload]);

  // Toggle auto-refresh setting
  const handleToggleAutoRefresh = useCallback(async (): Promise<void> => {
    setIsTogglingAutoRefresh(true);
    try {
      const response = await api.post(API_ENDPOINTS.USERS.AUTO_REFRESH, JSON.stringify({
        autoRefresh: !autoRefreshEnabled
      }));

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updatedUserData = await response.json();
      setUserData(updatedUserData.user);
    } catch (error) {
      reportAppError("Failed to update auto-refresh setting", error);
    } finally {
      setIsTogglingAutoRefresh(false);
    }
  }, [autoRefreshEnabled, api, setUserData]);

  // Fetch initial data status on mount (one-time, not polling)
  useEffect(() => {
    const fetchInitialStatus = async () => {
      try {
        const response = await api.get(`/shared/data/timer/${userData?.region}`);
        if (response.ok) {
          const data: BackendTimerResponse = await response.json();
          setDataStatus({
            wagoVersion: data.wagoVersion || "Unknown",
            tsmTimestamp: data.tsmTimestamp || 0,
            blizzardTimestamp: data.blizzardTimestamp || 0
          });
          // Sync the countdown timer with the server's latest completed update.
          // lastModified (from localStorage) can be stale after a long disconnect.
          const serverTs = data.blizzardTimestamp || 0;
          if (serverTs > lastModified) {
            setLastModified(serverTs);
          }
        }
      } catch (error) {
        reportAppError("Failed to fetch initial timer data", error, 'warning');
      }
    };

    if (userData?.region) {
      fetchInitialStatus();
    }
  }, [api, lastModified, setLastModified, userData?.region]);

  // Reset reload states when lastModified changes
  useEffect(() => {
    if (lastModified && dataStatus?.blizzardTimestamp === lastModified) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- resetting transient UI state when external timestamp catches up
      setIsReloadReady(false);
      setIsReloading(false);
    }
  }, [lastModified, dataStatus?.blizzardTimestamp]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (reloadTimeoutRef.current !== undefined) {
        clearTimeout(reloadTimeoutRef.current);
      }
      if (hoverTimeoutRef.current !== undefined) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div
      className="relative inline-flex items-center gap-2"
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Main Icon with Countdown - Always Visible; click/Enter toggles the pane */}
      <button
        type="button"
        onClick={handleTriggerClick}
        aria-label={t('common:nextUpdate.nextDataUpdate')}
        aria-expanded={showDetailPane}
        aria-haspopup="dialog"
        className="flex items-center gap-2 px-3 py-2 hover:bg-fp-card rounded-lg transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <Database className={`w-5 h-5 ${getCountdownColor()}`} />
        {countdown ? (
          <span className={`hidden md:inline font-mono text-sm ${getCountdownColor()}`}>
            {countdown}
          </span>
        ) : (
          isReloading && <LoadingSpinner />
        )}
      </button>
      {!countdown && !autoRefreshEnabled && isReloadReady && !isReloading && (
        <button
          onClick={handleManualRefresh}
          className="hover:bg-fp-border rounded-lg transition-colors"
          aria-label={t('common:nextUpdate.refreshData')}
        >
          <RefreshCw className="w-5 h-5 text-green-400 animate-pulse" />
        </button>
      )}

      {/* Detail Pane - Hover to show */}
      {showDetailPane && (
        <div className="absolute top-full left-0 mt-2 w-80 bg-fp-card border border-fp-border rounded-lg shadow-xl z-100 p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-white">{t('common:nextUpdate.dataSourceStatus')}</h3>
            <ConnectionIndicator isConnected={isConnected} />
          </div>

          {dataStatus ? (
            <div className="space-y-3">
              {/* TSM Data */}
              <div className="flex justify-between items-center">
                <span className="text-gray-300 text-sm">{t('common:nextUpdate.tsmData')}</span>
                <TsmStatusIcon timestamp={dataStatus.tsmTimestamp} />
              </div>

              {/* Blizzard Realm Data */}
              <div className="flex justify-between items-center">
                <span className="text-gray-300 text-sm">{t('common:nextUpdate.blizzardRealm')}</span>
                <span className={`font-mono text-sm ${getBlizzardColor(lastModified)}`}>
                  {formatTimestamp(lastModified)}
                </span>
              </div>

              {/* Connection Status */}
              <div className="flex justify-between items-center">
                <span className="text-gray-300 text-sm">{t('common:nextUpdate.liveUpdates')}</span>
                <div className="flex items-center gap-2">
                  <span className={`text-sm ${isConnected ? 'text-green-400' : 'text-yellow-400'}`}>
                    {isConnected ? t('common:nextUpdate.connected') : t('common:nextUpdate.reconnecting')}
                  </span>
                  {!isConnected && (
                    <button
                      onClick={reconnect}
                      className="text-xs text-blue-400 hover:text-blue-300"
                    >
                      {t('common:nextUpdate.retry')}
                    </button>
                  )}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-fp-border my-3" />

              {/* Auto-Refresh Toggle */}
              <div className="flex justify-between items-center">
                <span className="text-gray-300 text-sm">{t('common:nextUpdate.autoRefresh')}</span>
                <button
                  onClick={handleToggleAutoRefresh}
                  disabled={isTogglingAutoRefresh}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-800 ${autoRefreshEnabled ? "bg-blue-600" : "bg-gray-600"
                    } ${isTogglingAutoRefresh ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                  role="switch"
                  aria-checked={autoRefreshEnabled}
                  aria-label={t('common:nextUpdate.toggleAutoRefresh')}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${autoRefreshEnabled ? "translate-x-6" : "translate-x-1"
                      }`}
                  />
                </button>
              </div>

              {/* Status message */}
              <div className="text-xs text-gray-400 mt-2">
                {autoRefreshEnabled
                  ? t('common:nextUpdate.autoRefreshEnabled')
                  : t('common:nextUpdate.autoRefreshDisabled')}
              </div>
            </div>
          ) : (
            <div className="text-gray-400 text-sm">{t('common:nextUpdate.loadingStatus')}</div>
          )}
        </div>
      )}
    </div>
  );
};

NextUpdate.displayName = 'NextUpdate';

export default React.memo(NextUpdate);